import { useState } from "react";
import type { Node } from "@xyflow/react";
import { useQuery } from "@tanstack/react-query";
import { agentsApi } from "../../api/agents";
import { graphsApi } from "../../api/graphs";
import { usePractice } from "../../context/PracticeContext";
import { queryKeys } from "../../lib/queryKeys";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface WfNodeConfigProps {
  node: Node;
  onUpdate: (data: Record<string, unknown>) => void;
  onClose: () => void;
}

/* ─── Reusable input primitives ─── */

function TextInput({ label, value, onChange, placeholder, helperText }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; helperText?: string;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="mt-1 block w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary" />
      {helperText && <p className="mt-1 text-[10px] text-muted-foreground">{helperText}</p>}
    </label>
  );
}

function SelectInput({ label, value, options, onChange }: {
  label: string; value: string; options: { value: string; label: string }[]; onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-1 block w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

function TextAreaInput({ label, value, onChange, placeholder, rows = 4, mono }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; rows?: number; mono?: boolean;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <textarea value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={rows}
        className={cn("mt-1 block w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary resize-none", mono && "font-mono text-xs")} />
    </label>
  );
}

function NumberInput({ label, value, onChange, placeholder, min, max, helperText }: {
  label: string; value: number | undefined; onChange: (v: number | undefined) => void; placeholder?: string; min?: number; max?: number; helperText?: string;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <input type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
        placeholder={placeholder} min={min} max={max}
        className="mt-1 block w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary" />
      {helperText && <p className="mt-1 text-[10px] text-muted-foreground">{helperText}</p>}
    </label>
  );
}

function CheckboxInput({ label, checked, onChange, description }: {
  label: string; checked: boolean; onChange: (v: boolean) => void; description?: string;
}) {
  return (
    <label className="flex items-start gap-2.5 cursor-pointer group">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 rounded border-border text-primary focus:ring-primary" />
      <div>
        <span className="text-xs font-medium text-foreground group-hover:text-primary transition-colors">{label}</span>
        {description && <p className="text-[10px] text-muted-foreground">{description}</p>}
      </div>
    </label>
  );
}

/* ─── Agent Reference Config (picks from agents built in Agent Studio) ─── */

function AgentRefConfig({ node, onUpdate }: Pick<WfNodeConfigProps, "node" | "onUpdate">) {
  const { selectedPracticeId } = usePractice();
  const data = node.data as Record<string, unknown>;
  const name = (data.name as string) ?? "";
  const agentId = (data.agentId as string) ?? "";
  const sourceType = (data.sourceType as string) ?? ""; // "graph" or "legacy"

  // Agent Studio graphs (filtered to agent-type graphs with "start" nodes)
  const { data: allGraphs } = useQuery({
    queryKey: queryKeys.graphs.list(selectedPracticeId!),
    queryFn: () => graphsApi.list(selectedPracticeId!),
    enabled: !!selectedPracticeId,
  });
  const studioAgents = (allGraphs ?? []).filter((g) => {
    const nodes = g.graphDefinition?.nodes;
    if (!nodes || nodes.length === 0) return false;
    return nodes.some((n) => n.type === "start") && !nodes.some((n) => n.type === "wf_start");
  });

  // Legacy agents (from the Agents list)
  const { data: legacyAgents } = useQuery({
    queryKey: queryKeys.agents.list(selectedPracticeId!),
    queryFn: () => agentsApi.list(selectedPracticeId!),
    enabled: !!selectedPracticeId,
  });

  // Build grouped options
  const options: { value: string; label: string }[] = [
    { value: "", label: "Select an agent..." },
  ];
  if (studioAgents.length > 0) {
    options.push({ value: "__header_studio", label: "── Agent Studio ──" });
    for (const g of studioAgents) {
      options.push({ value: `graph:${g.id}`, label: `${g.name}${g.status === "deployed" ? " ✓" : " (draft)"}` });
    }
  }
  if ((legacyAgents ?? []).length > 0) {
    options.push({ value: "__header_legacy", label: "── Configured Agents ──" });
    for (const a of legacyAgents ?? []) {
      options.push({ value: `legacy:${a.id}`, label: a.name });
    }
  }

  // Reconstruct the composite value for the select
  const compositeValue = sourceType === "graph" ? `graph:${agentId}` : sourceType === "legacy" ? `legacy:${agentId}` : agentId ? `legacy:${agentId}` : "";

  return (
    <div className="space-y-4">
      <TextInput label="Step Name" value={name} onChange={(v) => onUpdate({ name: v })} placeholder="e.g. Triage Patient" />
      <label className="block">
        <span className="text-xs font-medium text-muted-foreground">Agent</span>
        <select
          value={compositeValue}
          onChange={(e) => {
            const v = e.target.value;
            if (v.startsWith("graph:")) {
              const gId = v.slice(6);
              const graph = studioAgents.find((g) => g.id === gId);
              onUpdate({ agentId: gId, agentName: graph?.name ?? "", sourceType: "graph" });
            } else if (v.startsWith("legacy:")) {
              const aId = v.slice(7);
              const agent = legacyAgents?.find((a) => a.id === aId);
              onUpdate({ agentId: aId, agentName: agent?.name ?? "", sourceType: "legacy" });
            } else {
              onUpdate({ agentId: "", agentName: "", sourceType: "" });
            }
          }}
          className="mt-1 block w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="">Select an agent...</option>
          {studioAgents.length > 0 && (
            <optgroup label="Agent Studio">
              {studioAgents.map((g) => (
                <option key={g.id} value={`graph:${g.id}`}>
                  {g.name}{g.status === "deployed" ? "" : " (draft)"}
                </option>
              ))}
            </optgroup>
          )}
          {(legacyAgents ?? []).length > 0 && (
            <optgroup label="Configured Agents">
              {(legacyAgents ?? []).map((a) => (
                <option key={a.id} value={`legacy:${a.id}`}>{a.name}</option>
              ))}
            </optgroup>
          )}
        </select>
      </label>
      {agentId && sourceType === "graph" && (
        <p className="text-[10px] text-muted-foreground">
          This step will invoke the Agent Studio graph. The agent's LLM, prompts, and node configuration are managed in Agent Studio.
        </p>
      )}
      {agentId && sourceType !== "graph" && (
        <p className="text-[10px] text-muted-foreground">
          This step will invoke a configured agent. For full visual configuration, build the agent in Agent Studio instead.
        </p>
      )}
      <TextAreaInput
        label="Input Mapping"
        value={(data.inputMapping as string) ?? ""}
        onChange={(v) => onUpdate({ inputMapping: v })}
        placeholder="Map workflow context to agent input..."
        rows={3}
        mono
      />
    </div>
  );
}

/* ─── Decision Config ─── */

function DecisionConfig({ node, onUpdate }: Pick<WfNodeConfigProps, "node" | "onUpdate">) {
  const data = node.data as Record<string, unknown>;
  const name = (data.name as string) ?? "";

  return (
    <div className="space-y-4">
      <TextInput label="Decision Name" value={name} onChange={(v) => onUpdate({ name: v })} placeholder="e.g. Check Lab Results" />
      <TextAreaInput
        label="Condition Expression"
        value={(data.conditionExpression as string) ?? ""}
        onChange={(v) => onUpdate({ conditionExpression: v })}
        placeholder="e.g. data.labResult.value > data.criticalThreshold"
        rows={3}
        mono
      />
      <p className="text-[10px] text-muted-foreground">
        Deterministic condition. Evaluates to true/false to decide which branch to follow. Connect edges and label them for each branch.
      </p>
      <TextInput
        label="Default Branch"
        value={(data.defaultBranch as string) ?? "else"}
        onChange={(v) => onUpdate({ defaultBranch: v })}
        placeholder="else"
        helperText="Edge label to follow when no condition matches."
      />
    </div>
  );
}

/* ─── EHR Action Config ─── */

function EhrActionConfig({ node, onUpdate }: Pick<WfNodeConfigProps, "node" | "onUpdate">) {
  const data = node.data as Record<string, unknown>;
  const name = (data.name as string) ?? "";

  return (
    <div className="space-y-4">
      <TextInput label="Step Name" value={name} onChange={(v) => onUpdate({ name: v })} placeholder="e.g. Fetch Patient Record" />
      <SelectInput
        label="Action"
        value={(data.ehrAction as string) ?? "read"}
        options={[
          { value: "read", label: "Read (GET)" },
          { value: "write", label: "Write (PUT/POST)" },
          { value: "query", label: "Search (GET + params)" },
        ]}
        onChange={(v) => onUpdate({ ehrAction: v })}
      />
      <SelectInput
        label="Resource Type"
        value={(data.resourceType as string) ?? "Patient"}
        options={[
          { value: "Patient", label: "Patient" },
          { value: "Appointment", label: "Appointment" },
          { value: "Encounter", label: "Encounter" },
          { value: "Condition", label: "Condition" },
          { value: "Observation", label: "Observation (Lab)" },
          { value: "DocumentReference", label: "Document" },
          { value: "Coverage", label: "Coverage" },
          { value: "Claim", label: "Claim" },
          { value: "MedicationRequest", label: "Medication" },
        ]}
        onChange={(v) => onUpdate({ resourceType: v })}
      />
      <TextAreaInput
        label="Query / Identifier"
        value={(data.query as string) ?? ""}
        onChange={(v) => onUpdate({ query: v })}
        placeholder="e.g. {{workflow.patientId}}"
        rows={2}
        mono
      />
    </div>
  );
}

/* ─── Payer Action Config ─── */

function PayerActionConfig({ node, onUpdate }: Pick<WfNodeConfigProps, "node" | "onUpdate">) {
  const data = node.data as Record<string, unknown>;

  return (
    <div className="space-y-4">
      <TextInput label="Step Name" value={(data.name as string) ?? ""} onChange={(v) => onUpdate({ name: v })} />
      <SelectInput
        label="Payer Action"
        value={(data.payerAction as string) ?? "eligibility"}
        options={[
          { value: "eligibility", label: "Eligibility Check (270/271)" },
          { value: "claim_submit", label: "Claim Submission (837P)" },
          { value: "claim_status", label: "Claim Status (276/277)" },
          { value: "prior_auth", label: "Prior Authorization" },
          { value: "era", label: "ERA Processing (835)" },
        ]}
        onChange={(v) => onUpdate({ payerAction: v })}
      />
      <TextInput label="Payer ID" value={(data.payerId as string) ?? ""} onChange={(v) => onUpdate({ payerId: v })} placeholder="e.g. BCBS, Aetna" />
    </div>
  );
}

/* ─── Notification Config ─── */

function NotificationConfig({ node, onUpdate }: Pick<WfNodeConfigProps, "node" | "onUpdate">) {
  const data = node.data as Record<string, unknown>;

  return (
    <div className="space-y-4">
      <TextInput label="Step Name" value={(data.name as string) ?? ""} onChange={(v) => onUpdate({ name: v })} />
      <SelectInput
        label="Channel"
        value={(data.channel as string) ?? "email"}
        options={[
          { value: "email", label: "Email (HIPAA-compliant)" },
          { value: "sms", label: "SMS (Twilio)" },
          { value: "fax", label: "Clinical Fax" },
          { value: "alert", label: "Staff Alert" },
          { value: "in_app", label: "In-App Notification" },
        ]}
        onChange={(v) => onUpdate({ channel: v })}
      />
      <TextInput label="Recipient" value={(data.recipient as string) ?? ""} onChange={(v) => onUpdate({ recipient: v })} placeholder="e.g. {{patient.email}} or role:billing" />
      <TextInput label="Subject" value={(data.subject as string) ?? ""} onChange={(v) => onUpdate({ subject: v })} placeholder="Notification subject" />
      <TextAreaInput
        label="Body Template"
        value={(data.body as string) ?? ""}
        onChange={(v) => onUpdate({ body: v })}
        placeholder="Use {{variable}} for dynamic content"
        rows={4}
      />
    </div>
  );
}

/* ─── Human Review Config ─── */

function HumanReviewConfig({ node, onUpdate }: Pick<WfNodeConfigProps, "node" | "onUpdate">) {
  const data = node.data as Record<string, unknown>;

  return (
    <div className="space-y-4">
      <TextInput label="Review Step Name" value={(data.name as string) ?? ""} onChange={(v) => onUpdate({ name: v })} placeholder="e.g. Clinician Sign-off" />
      <SelectInput
        label="Reviewer Role"
        value={(data.reviewerRole as string) ?? "clinician"}
        options={[
          { value: "clinician", label: "Clinician / Physician" },
          { value: "nurse", label: "Nurse" },
          { value: "billing", label: "Billing Staff" },
          { value: "admin", label: "Practice Admin" },
          { value: "compliance", label: "Compliance Officer" },
          { value: "any", label: "Any Staff Member" },
        ]}
        onChange={(v) => onUpdate({ reviewerRole: v })}
      />
      <CheckboxInput
        label="Require Reason"
        checked={(data.requireReason as boolean) ?? true}
        onChange={(v) => onUpdate({ requireReason: v })}
        description="Reviewer must provide a reason when rejecting."
      />
      <NumberInput
        label="Timeout (minutes)"
        value={data.timeoutMinutes as number | undefined}
        onChange={(v) => onUpdate({ timeoutMinutes: v })}
        placeholder="60"
        min={1}
        helperText="Auto-escalate if not reviewed within this time."
      />
      <TextInput
        label="Escalation Target"
        value={(data.escalationTarget as string) ?? ""}
        onChange={(v) => onUpdate({ escalationTarget: v })}
        placeholder="e.g. role:admin"
        helperText="Who to escalate to on timeout."
      />
    </div>
  );
}

/* ─── Timer Config ─── */

function TimerConfig({ node, onUpdate }: Pick<WfNodeConfigProps, "node" | "onUpdate">) {
  const data = node.data as Record<string, unknown>;

  return (
    <div className="space-y-4">
      <TextInput label="Step Name" value={(data.name as string) ?? ""} onChange={(v) => onUpdate({ name: v })} />
      <SelectInput
        label="Delay Type"
        value={(data.delayType as string) ?? "fixed"}
        options={[
          { value: "fixed", label: "Fixed Delay" },
          { value: "until_time", label: "Until Specific Time" },
          { value: "cron", label: "Next Cron Match" },
        ]}
        onChange={(v) => onUpdate({ delayType: v })}
      />
      {(data.delayType as string) === "cron" ? (
        <TextInput label="Cron Expression" value={(data.cronExpression as string) ?? ""} onChange={(v) => onUpdate({ cronExpression: v })} placeholder="0 9 * * 1-5" />
      ) : (data.delayType as string) === "until_time" ? (
        <TextInput label="Until" value={(data.untilExpression as string) ?? ""} onChange={(v) => onUpdate({ untilExpression: v })} placeholder="e.g. appointment.startTime - 24h" />
      ) : (
        <NumberInput label="Delay (minutes)" value={data.delayMinutes as number | undefined} onChange={(v) => {
          onUpdate({ delayMinutes: v, delayDescription: v ? `${v} minutes` : "" });
        }} placeholder="5" min={1} />
      )}
    </div>
  );
}

/* ─── Data Transform Config ─── */

function DataTransformConfig({ node, onUpdate }: Pick<WfNodeConfigProps, "node" | "onUpdate">) {
  const data = node.data as Record<string, unknown>;

  return (
    <div className="space-y-4">
      <TextInput label="Step Name" value={(data.name as string) ?? ""} onChange={(v) => onUpdate({ name: v })} />
      <SelectInput
        label="Transform Type"
        value={(data.transformType as string) ?? "fhir_to_internal"}
        options={[
          { value: "fhir_to_internal", label: "FHIR R4 -> Internal" },
          { value: "internal_to_fhir", label: "Internal -> FHIR R4" },
          { value: "x12_to_internal", label: "X12 -> Internal" },
          { value: "internal_to_x12", label: "Internal -> X12" },
          { value: "custom", label: "Custom Expression" },
        ]}
        onChange={(v) => onUpdate({ transformType: v })}
      />
      <TextAreaInput
        label="Mapping Expression"
        value={(data.mappingExpression as string) ?? ""}
        onChange={(v) => onUpdate({ mappingExpression: v })}
        placeholder="{ output.patientName: input.name[0].text }"
        rows={5}
        mono
      />
    </div>
  );
}

/* ─── API Call Config ─── */

function ApiCallConfig({ node, onUpdate }: Pick<WfNodeConfigProps, "node" | "onUpdate">) {
  const data = node.data as Record<string, unknown>;

  return (
    <div className="space-y-4">
      <TextInput label="Step Name" value={(data.name as string) ?? ""} onChange={(v) => onUpdate({ name: v })} />
      <SelectInput
        label="Method"
        value={(data.method as string) ?? "POST"}
        options={[
          { value: "GET", label: "GET" },
          { value: "POST", label: "POST" },
          { value: "PUT", label: "PUT" },
          { value: "PATCH", label: "PATCH" },
          { value: "DELETE", label: "DELETE" },
        ]}
        onChange={(v) => onUpdate({ method: v })}
      />
      <TextInput label="URL" value={(data.url as string) ?? ""} onChange={(v) => onUpdate({ url: v })} placeholder="https://api.example.com/..." />
      <TextAreaInput
        label="Headers (JSON)"
        value={(data.headersJson as string) ?? ""}
        onChange={(v) => onUpdate({ headersJson: v })}
        placeholder='{"Authorization": "Bearer {{secrets.api_key}}"}'
        rows={3}
        mono
      />
      <TextAreaInput
        label="Body Template"
        value={(data.body as string) ?? ""}
        onChange={(v) => onUpdate({ body: v })}
        placeholder="JSON body with {{variables}}"
        rows={4}
        mono
      />
    </div>
  );
}

/* ─── Compliance Check Config ─── */

function ComplianceConfig({ node, onUpdate }: Pick<WfNodeConfigProps, "node" | "onUpdate">) {
  const data = node.data as Record<string, unknown>;

  return (
    <div className="space-y-4">
      <TextInput label="Step Name" value={(data.name as string) ?? ""} onChange={(v) => onUpdate({ name: v })} />
      <SelectInput
        label="Check Type"
        value={(data.checkType as string) ?? "hipaa_audit"}
        options={[
          { value: "hipaa_audit", label: "HIPAA Audit Log" },
          { value: "phi_check", label: "PHI Detection" },
          { value: "consent_verify", label: "Patient Consent" },
          { value: "authorization_check", label: "Authorization Required" },
          { value: "custom", label: "Custom Rule" },
        ]}
        onChange={(v) => onUpdate({ checkType: v })}
      />
      <SelectInput
        label="On Failure"
        value={(data.failAction as string) ?? "block"}
        options={[
          { value: "block", label: "Block & Alert" },
          { value: "warn", label: "Warn & Continue" },
          { value: "log", label: "Log Only" },
          { value: "escalate", label: "Escalate to Compliance" },
        ]}
        onChange={(v) => onUpdate({ failAction: v })}
      />
    </div>
  );
}

/* ─── Trigger Config ─── */

function TriggerConfig({ node, onUpdate }: Pick<WfNodeConfigProps, "node" | "onUpdate">) {
  const data = node.data as Record<string, unknown>;

  return (
    <div className="space-y-4">
      <TextInput label="Trigger Name" value={(data.name as string) ?? ""} onChange={(v) => onUpdate({ name: v })} placeholder="e.g. New Patient Created" />
      <SelectInput
        label="Trigger Type"
        value={(data.triggerType as string) ?? "webhook"}
        options={[
          { value: "webhook", label: "Webhook (External)" },
          { value: "schedule", label: "Scheduled (Cron)" },
          { value: "clinical_event", label: "Clinical Event" },
          { value: "claims_event", label: "Claims Event" },
          { value: "referral_event", label: "Referral Event" },
          { value: "manual", label: "Manual Start" },
        ]}
        onChange={(v) => onUpdate({ triggerType: v })}
      />
      {(data.triggerType as string) === "schedule" && (
        <TextInput label="Cron Expression" value={(data.cronExpression as string) ?? ""} onChange={(v) => onUpdate({ cronExpression: v })} placeholder="0 9 * * 1-5" />
      )}
      <TextAreaInput
        label="Filter Expression"
        value={(data.filterExpression as string) ?? ""}
        onChange={(v) => onUpdate({ filterExpression: v })}
        placeholder="e.g. event.type === 'lab_result' && event.priority === 'critical'"
        rows={3}
        mono
      />
    </div>
  );
}

/* ─── Main Config Drawer ─── */

const NODE_TYPE_LABELS: Record<string, string> = {
  agent_ref: "Agent Step",
  decision: "Decision (Conditional)",
  ehr_action: "EHR Action",
  payer_action: "Payer Action",
  notification: "Notification",
  human_review: "Human Review (HITL)",
  timer: "Timer / Wait",
  data_transform: "Data Transform",
  api_call: "API Call",
  compliance: "Compliance Check",
  wf_trigger: "Workflow Trigger",
  wf_start: "Start Node",
  wf_end: "End Node",
};

export function WorkflowNodeConfig({ node, onUpdate, onClose }: WfNodeConfigProps) {
  const nodeType = node.type ?? "agent_ref";

  return (
    <div className="w-80 shrink-0 border-l border-border bg-card/50 flex flex-col">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h3 className="text-sm font-semibold">{NODE_TYPE_LABELS[nodeType] ?? "Configuration"}</h3>
        <Button variant="ghost" size="icon-sm" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex-1 overflow-auto p-4">
        {nodeType === "agent_ref" && <AgentRefConfig node={node} onUpdate={onUpdate} />}
        {nodeType === "decision" && <DecisionConfig node={node} onUpdate={onUpdate} />}
        {nodeType === "ehr_action" && <EhrActionConfig node={node} onUpdate={onUpdate} />}
        {nodeType === "payer_action" && <PayerActionConfig node={node} onUpdate={onUpdate} />}
        {nodeType === "notification" && <NotificationConfig node={node} onUpdate={onUpdate} />}
        {nodeType === "human_review" && <HumanReviewConfig node={node} onUpdate={onUpdate} />}
        {nodeType === "timer" && <TimerConfig node={node} onUpdate={onUpdate} />}
        {nodeType === "data_transform" && <DataTransformConfig node={node} onUpdate={onUpdate} />}
        {nodeType === "api_call" && <ApiCallConfig node={node} onUpdate={onUpdate} />}
        {nodeType === "compliance" && <ComplianceConfig node={node} onUpdate={onUpdate} />}
        {nodeType === "wf_trigger" && <TriggerConfig node={node} onUpdate={onUpdate} />}
        {(nodeType === "wf_start" || nodeType === "wf_end") && (
          <p className="text-sm text-muted-foreground">
            This is a {nodeType === "wf_start" ? "start" : "end"} node. No additional configuration needed.
          </p>
        )}
      </div>
    </div>
  );
}
